import { formatTimezoneDateString } from "@cp/toolkit";
import type { Dispatch, FC, ReactNode, SetStateAction } from "react";

import { Button } from "../button";
import { Icon } from "../icon";
import { Spinner } from "../loading";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";
import type { SaveStatus } from "./use-autosave-state";

export interface AutosaveIndicatorProps {
  isAutosavePaused: boolean;
  trailingAction?: ReactNode;
  saveStatus: SaveStatus;
  setIsAutosavePaused: Dispatch<SetStateAction<boolean>>;
  updatedAt: string;
}

export const AutosaveIndicator: FC<AutosaveIndicatorProps> = ({
  isAutosavePaused,
  setIsAutosavePaused,
  saveStatus,
  trailingAction,
  updatedAt,
}) => (
  <div className="flex items-center justify-between text-xs text-muted-foreground">
    <div className="flex items-center gap-2">
      {isAutosavePaused ? (
        <>
          <Icon icon="pause_circle" className="text-warning" />
          <span className="text-warning">Autosave paused</span>
        </>
      ) : saveStatus === "saving" ? (
        <>
          <Spinner className="w-4 h-4" />
          <span>Saving</span>
        </>
      ) : saveStatus === "saved" ? (
        <>
          <Icon icon="cloud_done" className="text-success" />
          <span className="text-success">Saved</span>
        </>
      ) : saveStatus === "error" ? (
        <>
          <Icon icon="error" className="text-destructive" />
          <span className="text-destructive">Couldn't save</span>
        </>
      ) : (
        <>
          <Icon icon="cloud_done" />
          <span className="">Last saved at {formatTimezoneDateString(updatedAt, "MM/dd/yyyy hh:mm a")}</span>
        </>
      )}
    </div>
    <div className="flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="2xs"
            display="icon"
            onClick={() => setIsAutosavePaused(!isAutosavePaused)}
            className="-my-1"
          >
            <Icon icon={isAutosavePaused ? "play_circle" : "pause_circle"} className="text-sm" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{isAutosavePaused ? "Resume autosave" : "Pause autosave"}</TooltipContent>
      </Tooltip>
      {trailingAction}
    </div>
  </div>
);
